import { Archive, ArrowRight, Lock } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import type { SchoolYearState } from '../lib/types';
import { Section } from '../ui/Collapsible';
import { useParent } from './ParentContext';
import { SCHOOL_CALENDAR_CHANGED } from './SchoolCalendar';

/**
 * Closing out a school year (#623, part of #453), in Settings → School Settings: save each child's portfolio (and a
 * high schooler's transcript) to Homeschool documents, lock the year's records, start the next year and move each
 * child up a grade. A closed year can be reopened.
 */

const fmt = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const gradeText = (g: string) => (g ? `${g}${/grade|^k$|kindergarten|pre/i.test(g) ? '' : ' grade'}` : 'No grade set');

function useSchoolYear() {
  const [data, setData] = useState<SchoolYearState | null>(null);
  const load = useCallback(() => {
    api
      .get<SchoolYearState>('/api/school-year')
      .then(setData)
      .catch(() => setData(null));
  }, []);
  useEffect(() => {
    load();
    window.addEventListener(SCHOOL_CALENDAR_CHANGED, load);
    return () => window.removeEventListener(SCHOOL_CALENDAR_CHANGED, load);
  }, [load]);
  return { data, load };
}

export function CloseOutSettings({ embedded = false }: { embedded?: boolean }) {
  const { toast, refresh } = useParent();
  const { data, load } = useSchoolYear();
  const [next, setNext] = useState<{ start: string; end: string } | null>(null);
  const [bump, setBump] = useState(true);
  const [busy, setBusy] = useState(false);
  const [early, setEarly] = useState(false);
  useEffect(() => setNext(data?.next ?? null), [data?.next?.start, data?.next?.end]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data?.enabled) return null;
  const y = data.year;
  // The form shows from a month before the year's last day; until then, a line says when.
  const closeFrom = y ? new Date(Date.parse(`${y.end}T12:00:00Z`) - 30 * 86_400_000).toISOString().slice(0, 10) : '';
  const soon = !!y && (data.ended || new Date().toLocaleDateString('en-CA') >= closeFrom || early);
  const canClose = !!y && !data.closed && soon;
  const summary = !y ? 'Set the school year first' : data.closed ? `${y.label} closed` : data.ended ? `${y.label} ended` : `${y.label} open`;
  const grades = data.children.filter((c) => c.nextGrade && c.nextGrade !== c.grade);

  async function close() {
    if (!y) return;
    setBusy(true);
    try {
      const r = await api.post<{ label: string; documents: number }>('/api/school-year/close', { nextStart: next?.start ?? '', nextEnd: next?.end ?? '', bumpGrades: bump });
      toast({ text: `${r.label} is closed. ${r.documents} PDF${r.documents === 1 ? ' is' : 's are'} in Homeschool documents.`, tone: 'good' });
      window.dispatchEvent(new Event(SCHOOL_CALENDAR_CHANGED));
      load();
      void refresh();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function reopen(id: string, label: string) {
    if (!confirm(`Reopen ${label}? Its records can be changed again. The PDFs saved when it closed stay in Homeschool documents.`)) return;
    try {
      await api.post(`/api/school-year/reopen/${id}`);
      toast({ text: `${label} is open again.`, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <Section embedded={embedded} id="close-year" title="Close out the school year" icon={<Archive size={18} />} summary={summary}>
      <div className="stack close-year">
        {!y && <p className="muted">Set your school year’s first and last day above, and you can close it out when it ends.</p>}
        {canClose && (
          <div className="test-form stack">
            <div className="row-between wrap">
              <strong>Close out {y.label}</strong>
              <span className={`small ${data.ended ? 'warn-text' : 'muted'}`}>{data.ended ? `The year ended ${fmt(y.end)}` : `The year ends ${fmt(y.end)}`}</span>
            </div>
            <p className="muted small">
              Saves each child’s year to Homeschool documents, locks the year’s records, and starts the next year. You can reopen it any time.
              {!data.ended && ' It hasn’t ended yet: anything recorded after today won’t be in the PDFs.'}
            </p>
            <div className="close-kids">
              {data.children.map((c) => (
                <div key={c.id} className="close-kid stack tight">
                  <strong>
                    {c.name} · {gradeText(c.grade)}
                  </strong>
                  <span className="small">✓ Portfolio PDF ({c.days} days · {c.hours} h · {c.samples} sample{c.samples === 1 ? '' : 's'})</span>
                  {data.transcripts && <span className={`small ${c.transcript ? '' : 'muted'}`}>{c.transcript ? '✓ Transcript PDF' : '— No transcript courses'}</span>}
                </div>
              ))}
            </div>
            <p className="small close-line">
              <Lock size={14} aria-hidden /> Locks attendance, grades, the journal, the reading log and field trips from {fmt(y.start)} to {fmt(y.end)}.
            </p>
            {next && (
              <div className="field-row wrap-row">
                <label>
                  Next year starts
                  <input type="date" value={next.start} onChange={(e) => setNext({ ...next, start: e.target.value })} />
                </label>
                <label>
                  and ends
                  <input type="date" value={next.end} min={next.start} onChange={(e) => setNext({ ...next, end: e.target.value })} />
                </label>
              </div>
            )}
            {grades.length > 0 && (
              <label className="check">
                <input type="checkbox" checked={bump} onChange={(e) => setBump(e.target.checked)} />
                <span>
                  Move up a grade: {grades.map((c) => `${c.name} ${c.grade} → ${c.nextGrade}`).join(', ')}
                </span>
              </label>
            )}
            <div className="row-gap wrap">
              <button className="btn primary" disabled={busy || !data.children.length || (!!next && (!next.start || !next.end || next.start > next.end))} onClick={close}>
                <Archive size={16} /> {busy ? 'Making the PDFs…' : 'Close out the year'}
              </button>
              <span className="muted small">Takes a minute: the PDFs are made on the server.</span>
            </div>
          </div>
        )}
        {y && !data.closed && !soon && (
          <p className="muted small">
            {y.label} is open. You can close it out from {fmt(closeFrom)}, a month before it ends.{' '}
            <button type="button" className="link-btn small" onClick={() => setEarly(true)}>
              Close it out now
            </button>
          </p>
        )}
        {y && data.closed && <p className="muted">{y.label} is closed. Set next year’s dates above to start it.</p>}
        {data.closes.map((c) => (
          <div key={c.id} className="closed-year">
            <span className="small">
              <Lock size={14} aria-hidden /> <strong>{c.label}</strong> closed {fmt(new Date(c.closedAt).toLocaleDateString('en-CA'))}
              {c.closedBy && ` by ${c.closedBy}`} · {c.documents} PDF{c.documents === 1 ? '' : 's'} saved to Homeschool documents
            </span>
            <button className="btn small" onClick={() => reopen(c.id, c.label)}>
              Reopen
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

/** On the Portfolio tab: from the year's last day until it's closed, a pointer to closing it out. */
export function CloseOutNudge({ childName }: { childName: string }) {
  const { data } = useSchoolYear();
  if (!data?.enabled || !data.year || !data.ended || data.closed) return null;
  return (
    <Link to="/settings#close-year" className="card close-nudge">
      <Archive size={18} aria-hidden />
      <span className="grow">
        {data.year.label} ended {fmt(data.year.end)}. Close it out to save {childName}’s portfolio to Homeschool documents and start the next year.
      </span>
      <ArrowRight size={16} aria-hidden />
    </Link>
  );
}
